import React, { useState } from 'react';
import { IconButton, Modal, Pivot, PivotItem, ScrollablePane, Stack } from '@fluentui/react';
import { TimeUser } from '../../../../../api/generated/data-contracts';
import { DelegateConfiguration } from './DelegateConfiguration';
import { ApproverConfiguration } from './ApproverConfiguration';
import { AdminConfiguration } from './AdminConfiguration';
import { FunctionOr } from '../../../../../types/FunctionOr';
import { useConfigValuesState } from '../../../../../hooks/useConfigValue';
import { BasicUserInfo } from './BasicUserInfo';
import { SystemInformation } from './SystemInformation';

interface TabbedModalProps {
    isOpen: boolean;
    onDismiss: () => void;
    user: TimeUser;
    setUser: (update: FunctionOr<TimeUser>) => void;
}

export const UpdateUserModal: React.FC<TabbedModalProps> = ({ isOpen, onDismiss, user, setUser }) => {
    const [selectedTab, setSelectedTab] = useState('basic');

    const handleTabChange = (item?: PivotItem) => {
        if (item) {
            setSelectedTab(item.props.itemKey || 'basic');
        }
    };

    const {
        initialData: {
            aggregatedUser: { originalUser },
        },
    } = useConfigValuesState();

    const canAccessAdminConfig = originalUser.isAdmin;
    const canAccessDelegatesConfig = useAccessDelegatesConfig();

    return (
        <Modal isOpen={isOpen} onDismiss={onDismiss} isBlocking={false} containerClassName="tabbed-modal-container" topOffsetFixed={true}>
            <Stack styles={{ root: { width: 750, height: '60vh', position: 'relative' } }} tokens={{ padding: 20, childrenGap: 16 }}>
                <Stack horizontal horizontalAlign="space-between">
                    <h2 style={{ margin: 0 }}>Edit Resource: {user.name}</h2>
                    <IconButton iconProps={{ iconName: 'Cancel' }} onClick={onDismiss} />
                </Stack>
                <Stack styles={{ root: { flexGrow: 1, position: 'relative' } }}>
                    <ScrollablePane styles={{ root: { height: '100%', width: '100%' } }}>
                        <Pivot selectedKey={selectedTab} onLinkClick={handleTabChange} styles={{ itemContainer: { padding: 10 } }}>
                            {canAccessAdminConfig && (
                                <PivotItem itemKey="basic" headerText="Basic Information">
                                    <BasicUserInfo user={user} setUser={setUser} />
                                </PivotItem>
                            )}
                            {canAccessAdminConfig && (
                                <PivotItem itemKey="permissions" headerText="Permissions">
                                    <AdminConfiguration user={user} setUser={setUser} />
                                </PivotItem>
                            )}
                            {canAccessAdminConfig && (
                                <PivotItem itemKey="approval" headerText="Approval">
                                    <ApproverConfiguration user={user} setUser={setUser} />
                                </PivotItem>
                            )}
                            {canAccessDelegatesConfig && (
                                <PivotItem itemKey="delegation" headerText="Delegation">
                                    <DelegateConfiguration user={user} />
                                </PivotItem>
                            )}
                            {canAccessAdminConfig && (
                                <PivotItem itemKey="sys-info" headerText="System Information">
                                    <SystemInformation user={user} />
                                </PivotItem>
                            )}
                        </Pivot>
                    </ScrollablePane>
                </Stack>
            </Stack>
        </Modal>
    );
};

const useAccessDelegatesConfig = () => {
    const {
        initialData: {
            aggregatedUser: { delegatedUsers, originalUser },
        },
    } = useConfigValuesState();
    const hasDelegates = delegatedUsers.length > 0;
    const hasDelegatePermissions = originalUser.permissions?.delegates;
    const isAdmin = originalUser.isAdmin;
    return isAdmin || hasDelegates || hasDelegatePermissions;
};
